/**
 * Developer Tab
 *
 * Developer mode toggle, API URL override, API keys.
 */

import { useState, useEffect } from 'react';
import { Wrench, Check, RotateCcw } from 'lucide-react';
import { useSettings } from '../../../hooks/useSettings';
import { fetchApi } from '../../../api/fetch';
import { ApiKeysSettings } from '../../ApiKeysSettings';
import { btn } from '../buttonStyles';

export function DeveloperTab() {
  const {
    settings,
    toggleDevMode,
    setApiUrlOverride,
  } = useSettings();

  const [apiUrlInput, setApiUrlInput] = useState(settings.apiUrlOverride || '');
  const [success, setSuccess] = useState<string | null>(null);
  const [resetting, setResetting] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);

  async function handleResetOnboarding() {
    const ok = window.confirm(
      'Reset onboarding state?\n\n' +
        'This wipes all 5 completion flags + welcome-email tracking, ' +
        'and deletes wizard-source tasks (welcome / fallback / email-reply). ' +
        'You will be sent back to the wizard.',
    );
    if (!ok) return;
    setResetting(true);
    setResetError(null);
    try {
      await fetchApi<{ reset: boolean; deletedTasks: number }>('/onboarding/reset', {
        method: 'POST',
        body: JSON.stringify({}),
      });
      // Hard reload to /onboarding so the OnboardingContext refetches from a
      // clean cache. The previous react-router navigate() raced with the
      // un-awaited refetch — OnboardingWizard mounted with stale
      // wizardComplete=true and <Navigate to=/app> bounced the user back.
      window.location.assign('/onboarding');
    } catch (err) {
      // Endpoint returns 404 in production — show a useful message.
      const message = err instanceof Error ? err.message : String(err);
      setResetError(
        /not.?found|404/i.test(message)
          ? 'Reset is only available in development builds.'
          : `Reset failed: ${message}`,
      );
      setResetting(false);
    }
  }

  // Keyboard shortcut for dev mode (Ctrl+Shift+D)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'D') {
        e.preventDefault();
        toggleDevMode();
        setSuccess(settings.devMode ? 'Developer mode disabled' : 'Developer mode enabled');
        setTimeout(() => setSuccess(null), 2000);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleDevMode, settings.devMode]);

  const handleSetApiUrl = () => {
    const url = apiUrlInput.trim();
    setApiUrlOverride(url || undefined);
    setSuccess(url ? `API URL set to: ${url}` : 'API URL reset to default');
    setTimeout(() => setSuccess(null), 3000);
  };

  const handleResetApiUrl = () => {
    setApiUrlInput('');
    setApiUrlOverride(undefined);
    setSuccess('API URL reset to default');
    setTimeout(() => setSuccess(null), 2000);
  };

  return (
    <>
      {success && (
        <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-start gap-3">
          <Check className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-green-800 dark:text-green-200">{success}</p>
        </div>
      )}

      {/* Developer Mode Toggle */}
      <div className="bg-surface-raised-light dark:bg-surface-raised-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-lg p-6 space-y-4">
        <div className="flex items-center gap-2">
          <Wrench className="w-5 h-5 text-amber-600 dark:text-amber-400" />
          <h2 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark">
            Developer Mode
          </h2>
        </div>
        <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
          Toggle developer mode to access additional debugging tools. You can also press Ctrl+Shift+D (or Cmd+Shift+D).
        </p>

        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-text-primary-light dark:text-text-primary-dark">
              Enable developer mode
            </p>
            <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark mt-0.5">
              Shows API URL override and debug controls
            </p>
          </div>
          <button
            role="switch"
            aria-checked={settings.devMode}
            onClick={toggleDevMode}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              settings.devMode
                ? 'bg-amber-500'
                : 'bg-border-hover-light dark:bg-border-hover-dark'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
                settings.devMode ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>

      {/* API URL Override (only shown when dev mode is on) */}
      {settings.devMode && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-200 dark:border-amber-800 rounded-lg p-6 space-y-4">
          <h2 className="text-lg font-semibold text-amber-900 dark:text-amber-100">
            API URL Override
          </h2>
          <p className="text-sm text-amber-700 dark:text-amber-300">
            Override the API URL for local development or testing.
          </p>

          <div className="space-y-2">
            <label className="text-sm font-medium text-amber-800 dark:text-amber-200">
              API URL
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={apiUrlInput}
                onChange={(e) => setApiUrlInput(e.target.value)}
                placeholder="http://localhost:3000"
                className="flex-1 px-3 py-2 border border-amber-300 dark:border-amber-700 bg-surface-raised-light dark:bg-amber-900/30 text-text-primary-light dark:text-text-primary-dark rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
              />
              <button
                onClick={handleSetApiUrl}
                className={btn.primary.sm}
              >
                Set
              </button>
            </div>
            <button
              onClick={handleResetApiUrl}
              className="text-sm text-amber-700 dark:text-amber-300 hover:underline"
            >
              Reset to Default
            </button>
            <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
              Current: {settings.apiUrlOverride || 'Using default (VITE_API_URL or /api/v1)'}
            </p>
          </div>
        </div>
      )}

      {/* Onboarding reset — works in dev only; endpoint returns 404 in prod */}
      <div className="bg-surface-raised-light dark:bg-surface-raised-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-lg p-6 space-y-4">
        <div className="flex items-center gap-2">
          <RotateCcw className="w-5 h-5 text-text-tertiary-light dark:text-text-tertiary-dark" />
          <h2 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark">
            Reset onboarding
          </h2>
        </div>
        <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
          Wipes the 5 onboarding completion flags + welcome-email tracking, deletes
          wizard-source tasks, and sends you back to the wizard. Useful for testing
          the onboarding flow on an already-completed account.
        </p>
        <p className="text-xs text-text-tertiary-light dark:text-text-tertiary-dark">
          Available in development builds only. In production the API returns 404
          so this is a no-op.
        </p>
        {resetError && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-sm text-red-800 dark:text-red-200">
            {resetError}
          </div>
        )}
        <button
          onClick={handleResetOnboarding}
          disabled={resetting}
          className={btn.secondary.sm}
        >
          {resetting ? 'Resetting…' : 'Reset onboarding'}
        </button>
      </div>

      {/* API Keys Section */}
      <ApiKeysSettings />
    </>
  );
}
